import React, { useState } from 'react';
import { useAdminApi } from '../../hooks/useAdminApi';
import {
  X,
  Key,
  AlertCircle,
  Loader2,
  Copy,
  CheckCircle,
  Calendar
} from 'lucide-react';

const GenerateLicenseModal = ({ tenant, onClose, onSave }) => {
  const { post } = useAdminApi();
  const [formData, setFormData] = useState({
    plan: tenant?.plan || 'starter',
    duration: 12, // months
    maxEmployees: 50,
    features: {
      attendance: true,
      leave: true,
      payroll: false,
      recruitment: false,
      performance: false,
      training: false
    }
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generatedLicense, setGeneratedLicense] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'duration' || name === 'maxEmployees' ? parseInt(value) : value
    }));
  };

  const handleFeatureChange = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature]
      }
    }));
  };

  const handlePlanChange = (plan) => {
    // Set default features based on plan
    const planFeatures = {
      starter: {
        attendance: true,
        leave: true,
        payroll: false,
        recruitment: false,
        performance: false,
        training: false
      },
      professional: {
        attendance: true,
        leave: true,
        payroll: true,
        recruitment: true,
        performance: false,
        training: false
      },
      enterprise: {
        attendance: true,
        leave: true,
        payroll: true,
        recruitment: true,
        performance: true,
        training: true
      }
    };

    const planLimits = {
      starter: 50,
      professional: 200,
      enterprise: 9999
    };

    setFormData(prev => ({
      ...prev,
      plan,
      features: planFeatures[plan] || planFeatures.starter,
      maxEmployees: planLimits[plan] || 50
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setGenerating(true);

    try {
      const response = await post('/licenses/generate', {
        tenantId: tenant._id,
        plan: formData.plan,
        duration: formData.duration,
        maxEmployees: formData.maxEmployees,
        features: Object.keys(formData.features).filter(f => formData.features[f])
      });

      if (response.success) {
        setGeneratedLicense(response.license);
      } else {
        setError(response.error || 'Failed to generate license');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedLicense.licenseKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    if (generatedLicense) {
      onSave();
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-auto z-10">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-3">
                <Key className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Generate License</h2>
                <p className="text-sm text-gray-500">{tenant?.companyName}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {generatedLicense ? (
              // License Generated Success View
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">License Generated!</h3>
                <p className="text-gray-500 mb-6">The license key has been generated and assigned to the tenant.</p>

                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">License Key</span>
                    <button
                      onClick={handleCopy}
                      className="flex items-center text-purple-600 hover:text-purple-700 text-sm font-medium"
                    >
                      {copied ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                  <code className="block text-sm text-gray-900 font-mono bg-white p-3 rounded-lg border border-gray-200 break-all">
                    {generatedLicense.licenseKey}
                  </code>
                </div>

                <div className="grid grid-cols-2 gap-4 text-left bg-gray-50 rounded-xl p-4">
                  <div>
                    <p className="text-sm text-gray-500">Plan</p>
                    <p className="font-medium text-gray-900 capitalize">{generatedLicense.plan}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Duration</p>
                    <p className="font-medium text-gray-900">{formData.duration} months</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Max Employees</p>
                    <p className="font-medium text-gray-900">{generatedLicense.maxEmployees}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Expires</p>
                    <p className="font-medium text-gray-900">
                      {new Date(generatedLicense.expiresAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleClose}
                  className="mt-6 w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-colors"
                >
                  Done
                </button>
              </div>
            ) : (
              // License Generation Form
              <form onSubmit={handleSubmit}>
                {/* Plan Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Plan
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {['starter', 'professional', 'enterprise'].map((plan) => (
                      <button
                        key={plan}
                        type="button"
                        onClick={() => handlePlanChange(plan)}
                        className={`p-4 border-2 rounded-xl text-center transition-all ${
                          formData.plan === plan
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className={`font-medium capitalize ${
                          formData.plan === plan ? 'text-purple-700' : 'text-gray-700'
                        }`}>
                          {plan}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Duration */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Duration (months)
                  </label>
                  <select
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={1}>1 month</option>
                    <option value={3}>3 months</option>
                    <option value={6}>6 months</option>
                    <option value={12}>12 months</option>
                    <option value={24}>24 months</option>
                    <option value={36}>36 months</option>
                  </select>
                </div>

                {/* Max Employees */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Employees
                  </label>
                  <input
                    type="number"
                    name="maxEmployees"
                    value={formData.maxEmployees}
                    onChange={handleChange}
                    min="1"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Features */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Features Included
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(formData.features).map(([feature, enabled]) => (
                      <label
                        key={feature}
                        className={`flex items-center p-3 border rounded-xl cursor-pointer transition-all ${
                          enabled ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={() => handleFeatureChange(feature)}
                          className="sr-only"
                        />
                        <span className={`w-4 h-4 border-2 rounded flex items-center justify-center mr-3 ${
                          enabled ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                        }`}>
                          {enabled && <CheckCircle className="w-3 h-3 text-white" />}
                        </span>
                        <span className={`capitalize text-sm ${enabled ? 'text-purple-700' : 'text-gray-600'}`}>
                          {feature}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={generating}
                  className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-colors disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Key className="w-5 h-5 mr-2" />
                      Generate License
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateLicenseModal;
